import React, { Suspense } from 'react';
import { Box, FormLabel, lighten, styled } from '@mui/material';
import type { CountryCode } from 'libphonenumber-js';
import { Icon, IconSize } from '../Icon';
import { EndAdornment } from './components/EndAdornment';
import { FlagElement } from './components/FlagElement';

// Dynamically import MuiTelInput
const MuiTelInput = React.lazy(() =>
  import('mui-tel-input').then((module) => ({
    default: module.MuiTelInput,
  })),
);

export interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  showHelpIcon?: boolean;
  helpMessage?: string;
  disabled?: boolean;
  error?: boolean;
  preferredCountries?: CountryCode[];
  defaultCountry?: CountryCode;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  label?: string;
  ariaLabel?: string;
  helperText?: string;
  inputRef?: React.Ref<any>;
  placeholder?: string;
  id?: string;
  required?: boolean;
}

const EXCLUDED_COUNTRIES: CountryCode[] = [
  'CC',
  'VI',
  'SJ',
  'MF',
  'KN',
  'SH',
  'BL',
  'SX',
  'AC',
  'CG',
  'CD',
  'BQ',
  'XK',
  'YT',
  'PW',
  'JE',
  'IM',
];

const MuiTelInputStyled = styled(MuiTelInput)(({ theme, size }) => ({
  '& .MuiInputBase-root.Mui-disabled': {
    border: '1px solid #D0D5DD',
    borderRadius: '8px',
    backgroundColor: '#F2F4F7',
    WebkitTextFillColor: '#667085',
    '& .MuiInputBase-input': {
      borderRadius: '8px',
      backgroundColor: '#F2F4F7',
      WebkitTextFillColor: '#667085',
    },
    '& .MuiSvgIcon-root': {
      color: '#98A2B3',
    },
  },
  '& .MuiInputBase-root.Mui-error': {
    borderColor: '#FDA29B',
    '& .MuiInputBase-input': {
      borderColor: '#FDA29B',
    },
  },
  margin: 0,
  '& .MuiInputBase-root': {
    paddingLeft: 0,
    '&:before, &:after': {
      display: 'none',
    },
    backgroundColor: '#ffffff',
    border: '1px solid #D0D5DD',
    borderRadius: '8px',
    transition: 'box-shadow 0.2s ease-in-out',
    '&:focus-within': {
      boxShadow: `0 0 0 1px ${lighten(theme.palette.primary.main, 0.5)}, 0 0 0 4px ${lighten(
        theme.palette.primary.main,
        0.8,
      )}`,
      '&.Mui-error': {
        boxShadow: `0px 1px 2px 0px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px rgba(240, 68, 56, 0.24)`,
        borderColor: '#FDA29B',
      },
      backgroundColor: '#fff',
      outline: 'none',
      borderColor: 'transparent',
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff !important',
    },
  },
  '& .MuiInputBase-input': {
    padding: size === 'medium' ? '12px' : '8px',
  },
  '& .MuiInputAdornment-root': {
    marginTop: '0 !important',
    marginLeft: '8px',
    marginRight: '0px',
  },
  '& .MuiFormHelperText-root': {
    marginLeft: '2px',
  },
  '& .MuiIconButton-root': {
    paddingRight: 0,
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  'input:-webkit-autofill': {
    borderRadius: '8px',
    transition: 'background-color 5000s',
  },
  '& .MuiTelInput-Flag > p': {
    marginBottom: 0,
  },
}));

export const PhoneInput: React.FC<PhoneInputProps> = ({
  showHelpIcon = false,
  helpMessage = 'Enter your phone number',
  value,
  disabled = false,
  error = false,
  onChange,
  preferredCountries,
  defaultCountry,
  size = 'medium',
  fullWidth = false,
  label = '',
  ariaLabel = '',
  helperText = '',
  inputRef,
  placeholder = '+491234567890',
  id,
  required = false,
}) => {
  const lastDefaultCountry = preferredCountries?.[preferredCountries.length - 1];
  /* multiple countries (UK, IM, JE) have the same dailing code +44,
  and it is preventing the library to display the UK flag automatically with only +44 prefix,
  so we need to display the UK flag manually
  */
  const trickToDisplayUnknownFlagAsGBFlag =
    placeholder === '+44' || value?.slice(0, 3)?.includes('+44') ? (
      <img
        width="24"
        height="24"
        src="https://img.icons8.com/color/48/great-britain-circular.png"
        alt="United Kingdom"
      />
    ) : (
      <Icon fullPath="/media/icons/design-system/phone-input/unknown-flag/" size={IconSize.MD} />
    );

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <Box sx={{ width: fullWidth ? '100%' : 'auto' }}>
        {label && (
          <FormLabel
            sx={{
              ml: '2px',
              mb: '4px',
              display: 'block',
              fontSize: '14px',
              fontWeight: '500',
              lineHeight: '20px',
              color: '#344054',
            }}
            htmlFor={id}
            error={error}
            required={required}
          >
            {label}
          </FormLabel>
        )}
        <MuiTelInputStyled
          placeholder={placeholder}
          helperText={helperText}
          fullWidth={fullWidth}
          defaultCountry={defaultCountry}
          preferredCountries={preferredCountries}
          onChange={(value) => onChange(value.replace(/ /g, ''))}
          value={value}
          error={error}
          disabled={disabled}
          variant="filled"
          slotProps={{
            htmlInput: {
              'aria-label': ariaLabel,
              'data-testid': 'phone',
            },
            input: {
              endAdornment: <EndAdornment error={error} showHelpIcon={showHelpIcon} helpMessage={helpMessage} />,
            },
          }}
          unknownFlagElement={trickToDisplayUnknownFlagAsGBFlag}
          getFlagElement={(isoCode, { countryName, isSelected }) => (
            <FlagElement isoCode={isoCode} countryName={countryName} isSelected={isSelected} />
          )}
          excludedCountries={EXCLUDED_COUNTRIES}
          size={size}
          MenuProps={{
            sx: {
              [`& #country-${lastDefaultCountry}`]: {
                borderBottom: '1px solid #EAECF0',
              },
            },
            disableScrollLock: true,
          }}
          inputRef={inputRef}
        />
      </Box>
    </Suspense>
  );
};
